import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useToken from "../../hooks/use-token";
import { useListInvestingAccountBrokerReportsQuery, useListInvestingAccountOperationsQuery } from "../../store";
import Badge from "../Badge";
import Banner from "../Banner";
import Card from "../Card";
import GhostButton from "../GhostButton";
import LinkButton from "../LinkButton";
import { RowSpinner } from "../Loader/Loader";
import { formatDate } from "./format";
import OperationDetails from "./OperationDetails";

const PAGE_SIZE = 50;
const INSTRUMENT_DEBOUNCE_MS = 300;
const STALE = new Set([404, 409]);

const yearOf = (value) => Number(/^(\d{4})/.exec(value || "")?.[1]);

function formatQuantity(quantity) {
  if (quantity === null || quantity === undefined) return "—";
  if (quantity > 0) return `+${quantity}`;
  if (quantity < 0) return `−${Math.abs(quantity)}`;
  return String(quantity);
}

function Amount({ amount, currency }) {
  if (amount === null || amount === undefined) return "—";
  const text = `${amount > 0 ? "+" : ""}${amount.toFixed(2)}${currency ? ` ${currency}` : ""}`;
  return <span className={clsx(amount > 0 && "text-success-ink")}>{text}</span>;
}

function CenteredCard({ children }) {
  return (
    <Card data-testid="centered-card" className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-text-muted">
      {children}
    </Card>
  );
}

// SCR-03 / SCR-05 «Операції»: one read-only chronology, filters over the whole of it, cursor pages
// (AC-10, AC-10b, AC-14, AC-14b). A 404 / 409 means the version is stale — the screen decides (onStale).
function OperationsTimeline({ accountId, syncUnitId, showOrigin = true, onStale }) {
  const { t } = useTranslation();
  const token = useToken();
  const [instrumentInput, setInstrumentInput] = useState("");
  const [instrument, setInstrument] = useState("");
  const [operationType, setOperationType] = useState("");
  const [taxYear, setTaxYear] = useState("");
  const [cursor, setCursor] = useState({ offset: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setInstrument(instrumentInput.trim()), INSTRUMENT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [instrumentInput]);

  // A new filter starts again from the first page.
  useEffect(() => setCursor({ offset: 0 }), [instrument, operationType, taxYear]);

  const { data: reportsData } = useListInvestingAccountBrokerReportsQuery({ accountId, syncUnitId }, { skip: !token });
  const { data, currentData, error, isFetching, refetch } = useListInvestingAccountOperationsQuery(
    {
      accountId,
      syncUnitId,
      instrument: instrument || undefined,
      operationTypes: operationType ? [operationType] : undefined,
      taxYear: taxYear || undefined,
      after: cursor.after,
      before: cursor.before,
      limit: PAGE_SIZE,
    },
    { skip: !token },
  );

  useEffect(() => {
    if (error && STALE.has(error.status) && onStale) onStale(error.status);
  }, [error, onStale]);

  const reportNames = useMemo(
    () => new Map((reportsData?.reports || []).map((report) => [report.id, report.fileName])),
    [reportsData],
  );
  const years = useMemo(() => {
    const all = new Set();
    (reportsData?.reports || []).forEach((report) => {
      for (let year = yearOf(report.periodStart); year <= yearOf(report.periodEnd); year++) all.add(year);
    });
    return [...all].filter(Number.isFinite).sort((a, b) => b - a);
  }, [reportsData]);

  if (error && !STALE.has(error.status)) {
    return (
      <Banner tone="danger">
        <p>{t("operationsTimeline.error")}</p>
        <LinkButton onClick={refetch}>{t("operationsTimeline.retry")}</LinkButton>
      </Banner>
    );
  }
  if (!data) {
    return error ? null : (
      <CenteredCard>
        <RowSpinner />
        {t("operationsTimeline.loading")}
      </CenteredCard>
    );
  }

  // Counts come from the whole chronology without filters (AC-14b): they never change with a filter or a page.
  const { totalCount, newCount, countsByType } = data;
  if (totalCount === 0) return <CenteredCard>{t("operationsTimeline.empty")}</CenteredCard>;

  const filtered = Boolean(instrument || operationType || taxYear);
  const typeName = (type) => t(`operationsTimeline.types.${type}`);
  const toggleChip = (type) => setOperationType((current) => (current === type ? "" : type));
  const pageData = currentData;
  const items = pageData?.items || [];

  return (
    <div className="flex flex-col gap-3">
      {showOrigin && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{t("operationsTimeline.allOperations")}</p>
            <p className="text-xs text-text-muted mt-0.5">
              {t("operationsTimeline.origin", { newCount, earlierCount: totalCount - newCount })}
            </p>
          </div>
          <span className="text-xs text-text-muted whitespace-nowrap">{t("operationsTimeline.readOnly")}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {countsByType.map(({ operationType: type, count }) => (
          <button
            key={type}
            type="button"
            aria-pressed={operationType === type}
            onClick={() => toggleChip(type)}
            className={clsx(
              "rounded-full px-3 py-1 text-xs font-medium",
              operationType === type ? "text-white bg-gradient-to-r from-cyan-650 to-cyan-75" : "bg-surface-alt text-text-muted hover:bg-border",
            )}
          >
            {`${typeName(type)} · ${count}`}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          aria-label={t("operationsTimeline.filters.instrument")}
          placeholder={t("operationsTimeline.filters.instrument")}
          maxLength={50}
          value={instrumentInput}
          onChange={(event) => setInstrumentInput(event.target.value)}
          className="rounded-lg border border-border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-450"
        />
        <select
          aria-label={t("operationsTimeline.filters.type")}
          value={operationType}
          onChange={(event) => setOperationType(event.target.value)}
          className="rounded-lg border border-border px-3 py-1.5 text-sm"
        >
          <option value="">{t("operationsTimeline.filters.allTypes")}</option>
          {countsByType.map(({ operationType: type }) => (
            <option key={type} value={type}>
              {typeName(type)}
            </option>
          ))}
        </select>
        <select
          aria-label={t("operationsTimeline.filters.year")}
          value={taxYear}
          onChange={(event) => setTaxYear(event.target.value)}
          className="rounded-lg border border-border px-3 py-1.5 text-sm"
        >
          <option value="">{t("operationsTimeline.filters.allYears")}</option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        <span className="ml-auto text-xs text-text-muted">
          {filtered && pageData
            ? t("operationsTimeline.matched", { matched: pageData.matchedCount, total: totalCount })
            : t("operationsTimeline.total", { count: totalCount })}
        </span>
      </div>

      {!pageData || isFetching ? (
        <CenteredCard>
          <RowSpinner />
          {t("operationsTimeline.loading")}
        </CenteredCard>
      ) : items.length === 0 ? (
        <CenteredCard>{t("operationsTimeline.emptyFiltered")}</CenteredCard>
      ) : (
        <>
          <Card data-testid="operations-table" className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-surface-muted text-xs text-text-muted">
                <tr>
                  {["date", "type", "instrument", "quantity", "amount", "details", "report", ...(showOrigin ? ["origin"] : [])].map((column) => (
                    <th
                      key={column}
                      className={clsx("px-3 py-2 font-medium text-left", (column === "quantity" || column === "amount") && "text-right")}
                    >
                      {t(`operationsTimeline.columns.${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((operation) => (
                  <tr key={operation.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(operation.operationDateTime)}</td>
                    <td className="px-3 py-2">{typeName(operation.operationType)}</td>
                    <td className="px-3 py-2">
                      {operation.ticker || operation.isin ? (
                        <>
                          {operation.ticker && <span className="font-semibold">{operation.ticker}</span>}
                          {operation.isin && <span className="block font-mono text-xs text-text-muted">{operation.isin}</span>}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{formatQuantity(operation.quantity)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Amount amount={operation.amount} currency={operation.currency} />
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted">
                      <OperationDetails operation={operation} />
                    </td>
                    <td className="px-3 py-2 text-xs">{reportNames.get(operation.sourceBrokerReportId) || "—"}</td>
                    {showOrigin && (
                      <td className="px-3 py-2">
                        {operation.sourceLinkType === "UPLOADED" ? (
                          <Badge tone="fresh">{t("operationsTimeline.origins.new")}</Badge>
                        ) : operation.sourceLinkType === "REUSED" ? (
                          <Badge tone="muted">{t("operationsTimeline.origins.earlier")}</Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          {(pageData.hasPrev || pageData.hasNext) && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-text-muted">
                {t("operationsTimeline.pager", {
                  from: cursor.offset + 1,
                  to: cursor.offset + items.length,
                  total: pageData.matchedCount,
                })}
              </span>
              <GhostButton
                className="ml-auto"
                disabled={!pageData.hasPrev}
                onClick={() => setCursor({ before: pageData.prevCursor, offset: Math.max(cursor.offset - PAGE_SIZE, 0) })}
              >
                {t("operationsTimeline.prev")}
              </GhostButton>
              <GhostButton
                disabled={!pageData.hasNext}
                onClick={() => setCursor({ after: pageData.nextCursor, offset: cursor.offset + items.length })}
              >
                {t("operationsTimeline.next")}
              </GhostButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default OperationsTimeline;
